import { useState, useEffect, useCallback, useRef } from "react";
import {
  Button,
  Card,
  Textarea,
  Select,
  Text,
  Checkbox,
  Spinner,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  Sparkle24Regular,
  ArrowUndo16Regular,
  ArrowSync16Regular,
  Clipboard16Regular,
  CheckmarkCircle24Regular,
  ErrorCircle24Regular,
  DismissCircle16Regular,
} from "@fluentui/react-icons";
import { marked } from "marked";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { getEmailContext, insertContent } from "@/office/mail-context";
import { getProjects } from "@/services/api";
import {
  connectAssistant,
  sendAssistantMessage,
  joinThread,
} from "@/services/socket";
import type { EmailContext, Tone } from "@/types";

const useStyles = makeStyles({
  root: {
    padding: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  contextCard: {
    padding: "10px",
    backgroundColor: "#F5F3FF",
    border: "1px solid #E0DAFB",
  },
  contextLabel: {
    fontWeight: 700,
    color: "#6366F1",
    marginBottom: "4px",
  },
  contextItem: {
    color: tokens.colorNeutralForeground2,
    lineHeight: 1.5,
  },
  row: {
    display: "flex",
    gap: "8px",
  },
  field: {
    flex: 1,
  },
  fieldLabel: {
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    marginBottom: "2px",
    display: "block",
  },
  generatedContent: {
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: "8px",
    padding: "10px",
    maxHeight: "240px",
    overflowY: "auto" as const,
    whiteSpace: "pre-wrap" as const,
    lineHeight: 1.6,
  },
  refineBtns: {
    display: "flex",
    gap: "4px",
  },
  refineBtn: {
    flex: 1,
    minWidth: 0,
  },
  successRoot: {
    textAlign: "center" as const,
    padding: "24px 0",
  },
  errorCard: {
    padding: "12px",
    backgroundColor: "#FEF2F2",
    border: "1px solid #FECACA",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: "8px",
    textAlign: "center" as const,
  },
  errorDismiss: {
    alignSelf: "flex-end" as const,
    minWidth: 0,
    padding: "2px",
  },
});

type Step = "compose" | "generating" | "review" | "done" | "error";

export function ComposePanel() {
  const styles = useStyles();
  const { user } = useAuth();

  const [step, setStep] = useState<Step>("compose");
  const [prompt, setPrompt] = useState("");
  const [tone, setTone] = useState<Tone>("Professional");
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [useKnowledgeBase, setUseKnowledgeBase] = useState(true);
  const [useProjectContext, setUseProjectContext] = useState(true);
  const [emailContext, setEmailContext] = useState<EmailContext | null>(null);
  const [generated, setGenerated] = useState("");
  const [threadId, setThreadId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingStatus, setLoadingStatus] = useState("Generating...");

  // Track a generation timeout so we can detect stalled responses
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearGenerationTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const startGenerationTimeout = useCallback(() => {
    clearGenerationTimeout();
    // If no response chunk arrives within 45 seconds, treat as timeout
    timeoutRef.current = setTimeout(() => {
      setStep("error");
      setErrorMessage("The request timed out. The server may be busy — please try again.");
    }, 45_000);
  }, [clearGenerationTimeout]);

  // Load email context from Office.js
  useEffect(() => {
    getEmailContext()
      .then(setEmailContext)
      .catch(() => {});
  }, []);

  // Load projects for the selector
  const { data: projects = [] } = useQuery({
    queryKey: ["projects"],
    queryFn: () => getProjects(user!.accessToken),
    enabled: !!user,
  });

  // Connect to WebSocket
  useEffect(() => {
    if (!user) return;

    let socket: ReturnType<typeof connectAssistant>;
    try {
      socket = connectAssistant(user.accessToken);
    } catch {
      setStep("error");
      setErrorMessage("Could not connect to the AI service. Check your internet connection and try again.");
      return;
    }

    // Listen for AI response chunks (streaming)
    socket.on("assistant:ai-response-chunk", (payload: { chunk: string }) => {
      clearGenerationTimeout();
      setGenerated((prev) => prev + payload.chunk);
    });

    socket.on(
      "assistant:ai-response-complete",
      (payload: { text?: string }) => {
        clearGenerationTimeout();
        // If no chunks were streamed, the full response comes in the complete event
        if (payload.text) {
          setGenerated((prev) => prev || payload.text!);
        }
        setStep("review");
      }
    );

    socket.on(
      "assistant:thread-created",
      (payload: { threadId: string }) => {
        setThreadId(payload.threadId);
        joinThread(payload.threadId);
      }
    );

    socket.on("assistant:chat-error", (payload?: { error?: string }) => {
      clearGenerationTimeout();
      const msg = payload?.error || "Something went wrong while generating. Please try again.";
      setErrorMessage(msg);
      setStep("error");
    });

    // Dynamic loading status from backend progress events
    socket.on("assistant:thinking-progress", (payload: { message?: string }) => {
      clearGenerationTimeout();
      startGenerationTimeout();
      if (payload.message) setLoadingStatus(payload.message);
    });

    socket.on("assistant:keepalive", (payload: { message?: string }) => {
      clearGenerationTimeout();
      startGenerationTimeout();
      if (payload.message) setLoadingStatus(payload.message);
    });

    // Handle connection-level errors
    socket.on("connect_error", () => {
      if (step === "generating") {
        clearGenerationTimeout();
        setErrorMessage("Lost connection to the AI service. Please check your network and try again.");
        setStep("error");
      }
    });

    socket.on("disconnect", (reason: string) => {
      // Only show error if we were mid-generation
      if (step === "generating" && reason !== "io client disconnect") {
        clearGenerationTimeout();
        setErrorMessage("Connection was interrupted. Please try again.");
        setStep("error");
      }
    });

    return () => {
      clearGenerationTimeout();
      socket.off("assistant:ai-response-chunk");
      socket.off("assistant:ai-response-complete");
      socket.off("assistant:thread-created");
      socket.off("assistant:chat-error");
      socket.off("assistant:thinking-progress");
      socket.off("assistant:keepalive");
      socket.off("connect_error");
      socket.off("disconnect");
    };
  }, [user, step, clearGenerationTimeout, startGenerationTimeout]);

  const handleGenerate = useCallback(() => {
    if (!prompt.trim() || !selectedProjectId) return;

    // Clear any previous error
    setErrorMessage(null);
    setStep("generating");
    setGenerated("");
    setLoadingStatus("Generating...");
    startGenerationTimeout();

    // Build the AI prompt with email context
    const contextParts: string[] = [];
    if (emailContext) {
      if (emailContext.to.length > 0) {
        contextParts.push(`Recipients: ${emailContext.to.join(", ")}`);
      }
      if (emailContext.subject) {
        contextParts.push(`Subject: ${emailContext.subject}`);
      }
      if (emailContext.body) {
        contextParts.push(
          `Existing email thread:\n${emailContext.body.slice(0, 2000)}`
        );
      }
    }

    const fullPrompt = [
      `Generate an email with the following instructions: ${prompt}`,
      `Tone: ${tone}`,
      selectedProjectId &&
        `Project context: Use data from project ${selectedProjectId}`,
      useKnowledgeBase && "Use the company knowledge base for context.",
      contextParts.length > 0 &&
        `Current email context:\n${contextParts.join("\n")}`,
      "Return ONLY the email body text, no subject line, no markdown formatting.",
    ]
      .filter(Boolean)
      .join("\n\n");

    try {
      if (threadId) {
        sendAssistantMessage({
          threadId,
          message: fullPrompt,
          projectId: selectedProjectId || undefined,
          source: "outlook-addin-email-composer",
        });
      } else {
        sendAssistantMessage({
          createThread: true,
          message: fullPrompt,
          projectId: selectedProjectId || undefined,
          optimisticThreadId: `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          source: "outlook-addin-email-composer",
        });
      }
    } catch {
      clearGenerationTimeout();
      setErrorMessage("Failed to send the request. Make sure you're connected and try again.");
      setStep("error");
    }
  }, [prompt, tone, selectedProjectId, useKnowledgeBase, emailContext, threadId, startGenerationTimeout, clearGenerationTimeout]);

  const handleInsert = useCallback(async () => {
    try {
      // Convert markdown to proper HTML so **bold** becomes <strong>, etc.
      const html = await marked.parse(generated, { breaks: true });
      await insertContent(html);
      setStep("done");
    } catch {
      setErrorMessage("Failed to insert content into the email. Please try copying the text manually.");
      setStep("error");
    }
  }, [generated]);

  const handleReset = useCallback(() => {
    setStep("compose");
    setGenerated("");
    setPrompt("");
    setErrorMessage(null);
  }, []);

  const handleRetry = useCallback(() => {
    setErrorMessage(null);
    // If we have prompt content, go back to compose so the user can hit generate again
    setStep("compose");
  }, []);

  const handleRefine = useCallback(
    (instruction: string) => {
      if (!threadId) return;
      setErrorMessage(null);
      setStep("generating");
      setGenerated("");
      setLoadingStatus("Refining...");
      startGenerationTimeout();
      try {
        sendAssistantMessage({
          threadId,
          message: `Refine the previous email: ${instruction}. Return ONLY the updated email body text.`,
          source: "outlook-addin-email-composer",
        });
      } catch {
        clearGenerationTimeout();
        setErrorMessage("Failed to send the refinement request. Please try again.");
        setStep("error");
      }
    },
    [threadId, startGenerationTimeout, clearGenerationTimeout]
  );

  const dismissError = useCallback(() => {
    setErrorMessage(null);
    if (step === "error") {
      setStep("compose");
    }
  }, [step]);

  return (
    <div className={styles.root}>
      {/* Inline error banner (dismissible, shown on any step) */}
      {errorMessage && step !== "error" && (
        <Card className={styles.errorCard} size="small">
          <Button
            className={styles.errorDismiss}
            appearance="transparent"
            icon={<DismissCircle16Regular />}
            size="medium"
            onClick={dismissError}
            style={{ color: "#DC2626" }}
            title="Dismiss"
          />
          <Text size={200} style={{ color: "#991B1B" }}>{errorMessage}</Text>
        </Card>
      )}

      {/* Detected Email Context */}
      {emailContext && (emailContext.to.length > 0 || emailContext.subject) && (
        <Card className={styles.contextCard} size="small">
          <Text className={styles.contextLabel} size={100}>
            DETECTED CONTEXT
          </Text>
          {emailContext.to.length > 0 && (
            <Text className={styles.contextItem} size={200} block>
              <strong>To:</strong> {emailContext.to.join(", ")}
            </Text>
          )}
          {emailContext.subject && (
            <Text className={styles.contextItem} size={200} block>
              <strong>Subject:</strong> {emailContext.subject}
            </Text>
          )}
        </Card>
      )}

      {/* Compose Step */}
      {step === "compose" && (
        <>
          <div>
            <Text className={styles.fieldLabel} size={200}>
              What do you want to say?
            </Text>
            <Textarea
              value={prompt}
              onChange={(_, data) => setPrompt(data.value)}
              placeholder="e.g. Follow up on the tender submission, ask about timeline..."
              resize="vertical"
              style={{ width: "100%" }}
              rows={3}
            />
          </div>

          <div className={styles.row}>
            <div className={styles.field}>
              <Text className={styles.fieldLabel} size={100}>
                Tone
              </Text>
              <Select
                value={tone}
                onChange={(_, data) => setTone(data.value as Tone)}
                size="medium"
              >
                {(
                  [
                    "Professional",
                    "Friendly",
                    "Concise",
                    "Persuasive",
                    "Formal",
                  ] as Tone[]
                ).map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </div>

            <div className={styles.field}>
              <Text className={styles.fieldLabel} size={100}>
                Project
              </Text>
              <Select
                value={selectedProjectId}
                onChange={(_, data) => setSelectedProjectId(data.value)}
                size="medium"
              >
                <option value="" disabled>
                  Select a project...
                </option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <Checkbox
            checked={useKnowledgeBase}
            onChange={(_, data) =>
              setUseKnowledgeBase(data.checked as boolean)
            }
            label="Use knowledge base context"
            size="medium"
          />
          <Checkbox
            checked={useProjectContext}
            onChange={(_, data) =>
              setUseProjectContext(data.checked as boolean)
            }
            label="Include project data"
            size="medium"
          />

          <Button
            appearance="primary"
            icon={<Sparkle24Regular />}
            onClick={handleGenerate}
            disabled={!prompt.trim() || !selectedProjectId}
            style={{
              width: "100%",
              background: "linear-gradient(135deg, #6366F1, #8B5CF6)",
            }}
          >
            Generate Email
          </Button>
        </>
      )}

      {/* Generating Step */}
      {step === "generating" && (
        <>
          <div style={{ textAlign: "center", padding: "8px 0" }}>
            <Spinner size="small" label={loadingStatus} />
          </div>
          {generated && (
            <div className={styles.generatedContent}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {generated}
              </ReactMarkdown>
            </div>
          )}
        </>
      )}

      {/* Review Step */}
      {step === "review" && (
        <>
          <Text size={200} weight="semibold">
            Generated Content
          </Text>
          <div className={styles.generatedContent}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {generated}
            </ReactMarkdown>
          </div>

          <div className={styles.row}>
            <Button
              appearance="primary"
              icon={<Clipboard16Regular />}
              onClick={handleInsert}
              style={{
                flex: 1,
                background: "linear-gradient(135deg, #6366F1, #8B5CF6)",
              }}
            >
              Insert into Email
            </Button>
            <Button
              appearance="secondary"
              icon={<ArrowUndo16Regular />}
              onClick={() => {
                setStep("compose");
                setGenerated("");
              }}
            >
              Redo
            </Button>
          </div>

          <div className={styles.refineBtns}>
            {["Shorter", "More formal", "Add CTA"].map((label) => (
              <Button
                key={label}
                className={styles.refineBtn}
                appearance="subtle"
                size="medium"
                onClick={() => handleRefine(label)}
              >
                {label}
              </Button>
            ))}
          </div>
        </>
      )}

      {/* Error Step */}
      {step === "error" && (
        <div className={styles.successRoot}>
          <ErrorCircle24Regular
            style={{ fontSize: "32px", color: "#DC2626" }}
          />
          <Text size={300} weight="bold" block style={{ color: "#DC2626", marginTop: "8px" }}>
            Something went wrong
          </Text>
          <Text
            size={200}
            style={{ color: tokens.colorNeutralForeground3, marginTop: "4px" }}
            block
          >
            {errorMessage || "An unexpected error occurred. Please try again."}
          </Text>
          <div style={{ display: "flex", gap: "8px", justifyContent: "center", marginTop: "12px" }}>
            <Button
              appearance="primary"
              icon={<ArrowSync16Regular />}
              onClick={handleRetry}
              style={{ background: "linear-gradient(135deg, #6366F1, #8B5CF6)" }}
            >
              Try Again
            </Button>
          </div>
        </div>
      )}

      {/* Done Step */}
      {step === "done" && (
        <div className={styles.successRoot}>
          <CheckmarkCircle24Regular
            style={{ fontSize: "32px", color: "#059669" }}
          />
          <Text size={400} weight="bold" block style={{ color: "#059669", marginTop: "8px" }}>
            Content Inserted!
          </Text>
          <Text
            size={200}
            style={{ color: tokens.colorNeutralForeground3, marginTop: "4px" }}
            block
          >
            Review the email, edit as needed, then hit Send.
          </Text>
          <Button
            appearance="secondary"
            icon={<ArrowSync16Regular />}
            onClick={handleReset}
            style={{ marginTop: "12px" }}
          >
            Generate Another
          </Button>
        </div>
      )}
    </div>
  );
}
